import { css, html, LitElement, nothing } from 'lit'
import { property } from 'lit/decorators.js'
import { safeCustomElement } from '@/utilities/custom-element'
import type { FilterItemVariant } from './types'

@safeCustomElement('mdc-filter-item')
export class MdcFilterItem extends LitElement {
  @property({ type: String }) title = ''
  @property({ type: String }) variant: FilterItemVariant = 'base'
  @property({ type: String }) icon = ''
  @property({ type: Boolean }) selected = false
  @property({ type: Boolean, attribute: 'show-close' }) showClose = false

  static styles = css`
		:host {
			display: block;
			font-family: var(--typography-font-family-sans);
		}

		.mdc-filter-item {
			display: flex;
			align-items: center;
			border-radius: 0.5rem;
			padding: 0.75rem;
			background-color: var(--color-functional-gray-white, #ffffff);
			transition: background-color 0.3s ease;
			gap: 0.5rem;
			border: none;
			width: 100%;
			cursor: pointer;
			margin: 0;
		}

		.mdc-filter-item:hover {
			background-color: var(--color-functional-gray-25, #fafafa);
		}

		.mdc-filter-item--selected {
			background-color: var(--color-functional-gray-25, #fafafa);
		}

		.mdc-filter-item__icon {
			display: flex;
			align-items: center;
			justify-content: center;
			flex-shrink: 0;
		}

		.mdc-filter-item__title {
			font-size: 0.875rem;
			color: var(--color-functional-gray-black, #111111);
			font-weight: 450;
			line-height: 1.3;
			margin: 0;
			padding: 0;
		}

		.mdc-filter-item__close {
			display: flex;
			align-items: center;
			justify-content: center;
			flex-shrink: 0;
			width: 1.25rem;
			height: 1.25rem;
			border: none;
			background: none;
			cursor: pointer;
			padding: 0;
			margin-left: auto;
		}
	`

  private handleClose(e: Event) {
    e.stopPropagation()
    this.dispatchEvent(
      new CustomEvent('close', {
        detail: {},
        bubbles: true,
        composed: true,
      })
    )
  }

  render() {
    const classes = [
      'mdc-filter-item',
      this.selected ? 'mdc-filter-item--selected' : '',
      this.variant === 'checkable' ? 'mdc-filter-item--checkable' : '',
      this.variant === 'checkbox' ? 'mdc-filter-item--checkbox' : '',
      this.variant === 'link' ? 'mdc-filter-item--link' : '',
      this.variant === 'radio' ? 'mdc-filter-item--radio' : '',
      this.variant === 'toggle' ? 'mdc-filter-item--toggle' : '',
      this.variant === 'with-arrow' ? 'mdc-filter-item--with-arrow' : '',
      this.variant === 'worked-with' ? 'mdc-filter-item--worked-with' : '',
    ]
      .filter(Boolean)
      .join(' ')

    return html`
			<button type="button" class=${classes}>
				${this.icon ? html`<div class="mdc-filter-item__icon"><slot name="icon">${this.icon}</slot></div>` : nothing}
				<p class="mdc-filter-item__title">${this.title}</p>
				${
          this.showClose && this.selected
            ? html`
							<button type="button" class="mdc-filter-item__close" @click=${this.handleClose}>
								<svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
									<title>Close</title>
									<path d="M1 1L9 9M9 1L1 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
								</svg>
							</button>
						`
            : nothing
        }
			</button>
		`
  }
}
